import React, { ChangeEvent, useEffect, useMemo } from 'react';
import { FormInputRow } from 'components/input';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { IOption } from 'components/input/selectInput';
import {
  useIsSelectedSearchTargetScreenHits,
  useCurrentSearchId,
  usePerformSearch,
  useSearchName,
  useIsSearchInProgress,
  useSearchTargets,
  useSelectedSearchTarget,
  useSetSearchName,
  useSetSelectedSearchTarget,
  useSearchResult,
  useAllSelectedScores,
  useAllowEditSearch,
} from 'redux/hooks/searchnFilter';
import { SearchTargetDTO } from 'business/models/Models';
import { useNavigate } from 'react-router-dom';
import { useModalHandlers } from 'components/modal';
import {
  CrystalSearchTarget,
  ESearchPathFilters,
  ESnfRouteSegment,
  getSnfRoutePath,
  searchPathFilterOptions,
} from '../constants';

interface Props {
  selectedPathFilter: ESearchPathFilters;
  setSelectedPathFilter: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

let shouldRouteToResultPage = false;

const SearchInfo = (props: Props) => {
  const { selectedPathFilter, setSelectedPathFilter: handlePathFilterChange } = props;
  const navigate = useNavigate();
  const setSearchTargetId: (n: number) => void = useSetSelectedSearchTarget();
  const currentSearchId = useCurrentSearchId();
  const setSearchName: (n: string) => void = useSetSearchName();
  const selectedSearchTargetId: number = useSelectedSearchTarget();
  const searchTargetsList = useSearchTargets();
  const performSearch = usePerformSearch();
  const searchName: string = useSearchName();
  const IssearchInProgress = useIsSearchInProgress();
  const searchResult = useSearchResult();
  const { showModal, hideModal } = useModalHandlers();
  const selectedScores = useAllSelectedScores();
  const isSelectedSearchTargetScreenHits = useIsSelectedSearchTargetScreenHits();
  const allowEdits = useAllowEditSearch();

  const searchTargetsOptionsList: IOption[] = useMemo(
    () =>
      searchTargetsList
        .filter((x) => x.isVisible)
        .map((item: SearchTargetDTO) => ({ title: item.name, value: item.id })),
    [searchTargetsList],
  );

  // Handle case where selected search target is v3 Xtals
  useEffect(() => {
    if (
      selectedSearchTargetId &&
      searchTargetsList.length > 0 &&
      !searchTargetsList.find((x) => x.id === selectedSearchTargetId)
    ) {
      const xtalTarget = searchTargetsList.find((x) =>
        x.name.includes(CrystalSearchTarget),
      );
      setSearchTargetId(xtalTarget?.id);
    }
  }, [selectedSearchTargetId, searchTargetsList]);

  const handleOnTargetChange = (e: ChangeEvent<HTMLInputElement>) => {
    const targetId = parseInt(e.target.value);
    if (searchResult.rows.items.length) {
      showModal({
        heading: 'Search',
        body: 'Changing search target will clear all search settings. Continue?',
        scrollable: false,
        noButtonText: 'No',
        okButtonText: 'Yes',
        onOk: () => {
          setSearchTargetId(targetId);
          hideModal();
        },
      });
    } else {
      setSearchTargetId(targetId);
    }
  };
  const handleOnSearchNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchName(e.target.value);
  };

  const handleOnSearchButtonClick = () => {
    if (isSelectedSearchTargetScreenHits && Object.keys(selectedScores).length < 1) {
      showModal({
        heading: 'Search',
        body: (
          <div className="text-sm">
            <div className="bg-blue-200 text-blue-700 rounded-md px-3 py-1 text-sm">
              You must specify which scores identify a hit to run screen hits search
            </div>
            Please set the screen hits score in the screen hits score button to able run
            screen hits search.
          </div>
        ),
        scrollable: false,
        isCancelOnly: true,
        noButtonText: 'Close',
      });
    } else {
      shouldRouteToResultPage = true;
      if (performSearch() !== false) {
        document.body.style.cursor = 'wait';
      }
    }
  };

  useEffect(() => {
    if (!IssearchInProgress && searchResult.rows.items && shouldRouteToResultPage) {
      document.body.style.cursor = 'default';
      navigate(getSnfRoutePath(currentSearchId, ESnfRouteSegment.Result));
    }
  }, [searchResult]);

  useEffect(
    () => () => {
      shouldRouteToResultPage = false;
    },
    [],
  );

  return (
    <>
      <div id="searchinfo" className="w-full flex flex-row mt-3">
        <div id="searchinfo-wrapper" className="flex-row w-full">
          <div
            id="searchinfo-title"
            className="self-stretch h-[37px] text-black text-base font-bold leading-tight"
          >
            Search Info
          </div>
          <div
            id="searchinfo-form-input-row"
            className="w-full self-stretch flex-row justify-start items-start flex"
          >
            <FormInputRow
              labeltext="File Name"
              containerClassname="w-full flex gap-12"
              labelClassname="text-sm w-1/12 h-[15px]"
              inputClassname="rounded-sm w-full border-2 border-gray-300 h-[30px] self-stretch"
              inputContainerClass="w-11/12"
              onChange={handleOnSearchNameChange}
              value={searchName}
              disabled={!allowEdits}
            />
          </div>
        </div>
      </div>
      <hr
        id="searchinfo-hr-line"
        className="h-px my-2 bg-gray-300 border-0 dark:bg-gray-700"
      />
      <div id="searchinfo-search-wrapper" className="flex-row w-full flex">
        <div
          id="searchinfo-search-path-form-input-row"
          className="w-4/12 flex-row justify-start items-start flex"
        >
          <FormInputRow
            labeltext="Search Path"
            containerClassname="w-full gap-8"
            labelClassname="self-stretch text-black font-bold h-[15px]"
            inputType="select"
            inputClassname="rounded-sm border-2 border-gray-300 h-[30px] w-[90%]"
            options={searchPathFilterOptions}
            value={selectedPathFilter}
            onChange={handlePathFilterChange}
          />
        </div>
        <div id="searchinfo-search-target-form-input-row" className="w-7/12 flex">
          <FormInputRow
            labeltext="Search Target"
            containerClassname="w-full flex-start"
            labelClassname="self-stretch text-black font-bold w-[20.3%] h-[15px]"
            inputType="select"
            inputClassname="rounded-sm border-2 border-gray-300 h-[30px] self-stretch"
            inputContainerClass="w-full"
            value={selectedSearchTargetId}
            options={searchTargetsOptionsList}
            onChange={handleOnTargetChange}
          />
        </div>
        <div id="searchinfo-search-button" className="w-1/12 flex justify-end">
          <RMCustomButton
            contentContainerClass="tree-control-open-button h-[30px]"
            titleClass="tree-control-open-button-title"
            title={'Search'}
            onClick={handleOnSearchButtonClick}
            isDisabled={IssearchInProgress}
          />
        </div>
      </div>
    </>
  );
};
export default SearchInfo;
